import React from "react";
import { Outlet, matchRoutes } from "react-router-dom";
import { SplatRedirect } from "@dealer-center/core-custom-components";
import DashboardOverview from "./tabs/overview";
import { Hierarchy } from "./tabs/hierarchy";
import { DashboardDepartments } from "@dealer-center/ddc-pages-departments-react";
import { DashboardDealerCodes } from "@dealer-center/ddc-pages-dealer-codes-react";
import { DashboardResources } from "@dealer-center/ddc-pages-resources-react";
import { PagesInventoryConfigUi } from "@dealer-center/ddc-pages-inventory-config-react";
import { DashboardAccountMappings } from "@dealer-center/ddc-pages-service-account-mappings-react";
import { PagesAltTextManagerUiDisplay } from "@dealer-center/ddc-pages-alt-text-manager-react";

import type { RouteObject } from "react-router-dom";
import type { TabProps } from "@dealer-center/core-components";
import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import type { CC9FlagNames } from "@dealer-center/core-interfaces-third-party-launch-darkly";
import type { environments } from "@dealer-center/core-enviroment";
import type { ExperimentsWithEnabled } from "@dealer-center/core-pages-experiment-enablement-interface";
import { type DdcExperimentIdType } from "@dealer-center/core-pages-experiment-enablement-interface";

export type routes =
  | "overview"
  | "departments"
  | "accountMappings"
  | "resources"
  | "dealerCodes"
  | "hierarchy"
  | "websiteInventoryConfigs"
  | "altTextManager";

export const routeIndexMapping: Record<routes, number> = {
  overview: 0,
  departments: 1,
  accountMappings: 2,
  resources: 3,
  dealerCodes: 4,
  websiteInventoryConfigs: 5,
  hierarchy: 6,
  altTextManager: 7
};
interface tabMappingItem extends Partial<TabProps> {
  indexMapping: number;
  element?: React.ReactElement;
  route: routes | string;
  label: string;
  visibleLDToggle?: CC9FlagNames;
  visibleExperimentToggle?: DdcExperimentIdType;
}

const tabMapping: tabMappingItem[] = [
  {
    indexMapping: routeIndexMapping.overview,
    label: "Overview",
    route: "overview",
    element: <DashboardOverview />
  },
  {
    indexMapping: routeIndexMapping.departments,
    label: "Departments",
    route: "departments",
    element: <DashboardDepartments />
  },
  {
    indexMapping: routeIndexMapping.accountMappings,
    label: "Account Mappings",
    route: "accountMappings",
    element: <DashboardAccountMappings />
  },
  {
    indexMapping: routeIndexMapping.resources,
    label: "Resources",
    route: "resources",
    element: <DashboardResources />
  },
  {
    indexMapping: routeIndexMapping.dealerCodes,
    label: "Dealer Codes",
    route: "dealerCodes",
    element: <DashboardDealerCodes />
  },
  {
    indexMapping: routeIndexMapping.websiteInventoryConfigs,
    label: "Website Inventory Configs",
    route: "websiteInventoryConfigs/:selectedConfig?",
    element: <PagesInventoryConfigUi />
  },
  {
    indexMapping: routeIndexMapping.hierarchy,
    label: "Account Hierarchy",
    route: "hierarchy",
    element: <Hierarchy />
  },
  {
    indexMapping: routeIndexMapping.altTextManager,
    label: "Alt Text Manager",
    route: "altTextManager",
    element: <PagesAltTextManagerUiDisplay />,
    visibleLDToggle: "alt-text-manager",
    visibleExperimentToggle: "experiment-navigation-items"
  }
];
export const dashboardChildRoutes = (
  _enviroment: environments
): RouteObject[] => {
  return [
    ...tabMapping.map(tabItem => ({
      path: tabItem.route,
      element: (
        <>
          {tabItem.route !== "overview" && <h2>{tabItem.label}</h2>}
          {tabItem.element}
        </>
      )
    })),
    {
      // The dashboard has no index route, so every unmatched tab path — and
      // bare `/dashboard` itself — lands here. A relative <Navigate> would
      // resolve against the full matched path under react-router v7 and loop.
      path: "*",
      element: <SplatRedirect to="overview" />
    }
  ];
};

export function getSelectedTabFromLocation(
  accountEntity: Partial<AccountEntity> | undefined,
  location: Partial<Location>
) {
  const routes: {
    path: string;
    routeIndexMap: number;
  }[] = tabMapping.map(tabItem => {
    return {
      path: "/dealer/" + accountEntity?.id + "/dashboard/" + tabItem.route,
      routeIndexMap: tabItem.indexMapping
    };
  });

  if (!accountEntity || !accountEntity || !location.pathname) {
    return routeIndexMapping.overview;
  }
  const match = matchRoutes(routes, location.pathname);
  const tab = !match
    ? routeIndexMapping.overview
    : match[0].route.routeIndexMap;
  if (!tab) {
    return routeIndexMapping.overview;
  }
  return tab;
}

export function getListOfTabs(
  accountEntity: Partial<AccountEntity>,
  location: Partial<Location>,
  cc9Flags: Partial<Record<CC9FlagNames, boolean>>,
  experimentPreferences: readonly ExperimentsWithEnabled<DdcExperimentIdType>[]
): TabProps[] {
  const tab = getSelectedTabFromLocation(accountEntity, location);
  const isTabVisible = (tabItem: tabMappingItem) => {
    return (
      !tabItem.visibleLDToggle ||
      cc9Flags[tabItem.visibleLDToggle] === true ||
      !tabItem.visibleExperimentToggle ||
      experimentPreferences.find(
        experiment => experiment.id === tabItem.visibleExperimentToggle
      )?.enabled === true
    );
  };

  return tabMapping.filter(isTabVisible).map(tabItem => {
    return {
      label: tabItem.label,
      disabled: tabItem.disabled,
      active: tab === tabItem.indexMapping,
      component:
        tab === tabItem.indexMapping ? (
          <Outlet context={{ accountEntity }} />
        ) : (
          <></>
        )
    };
  });
}
